import {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import pdfMake from "pdfmake/build/pdfmake";

import {instance} from "../../api/apiSetup";
import {apiUrl} from "../../api/baseURL";
import {UI_PERMISSION_TYPE} from "../../Components/adminForms/Ba3aSettings/tabs/PositionsSettings/useRoutePermissions";
import {prepareLinks, preparePdfFileName} from "../../Components/KP/kp-f/kpFinTable/PdfPrevievModal";
import {ICON_NAMES} from "../generalConstants.js/generalConstants";
import {toaster} from "../hooks/toaster";

import IconButton from "./IconButton";

const ShareButton = ({
  className,
  fileArray,
  shareLink,
  fileName: fileNameRow,
  pdfFile,
  entityId,
  entityName,
  uiPermissionType = UI_PERMISSION_TYPE.READABLE,
}) => {
  const {t} = useTranslation();

  const fileName = preparePdfFileName(fileNameRow);
  const [isPreparing, setIsPreparing] = useState(false);
  const [blobObj, setBlobObj] = useState(null);
  const previewReqIdRef = useRef(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const reqId = ++previewReqIdRef.current;
    const parent = document.getElementById("pdfContainer");
    if (parent) parent.innerHTML = "";

    let objectUrl = null;

    if (pdfFile) {
      const renderIframe = src => {
        if (reqId !== previewReqIdRef.current) return;
        const iframe = document.createElement("iframe");
        const parent = document.getElementById("pdfContainer");
        if (!parent) return;
        iframe.src = src;
        iframe.classList.add("pdf-iframe");
        parent.appendChild(iframe);
      };

      if (typeof pdfFile.getBlob === "function") {
        setLoading(true);
        pdfFile.getBlob(blob => {
          if (reqId !== previewReqIdRef.current) return;
          objectUrl = URL.createObjectURL(blob);
          renderIframe(objectUrl);
          setLoading(false);
        });
      } else if (typeof pdfFile.getDataUrl === "function") {
        setLoading(true);
        pdfFile.getDataUrl(dataUrl => {
          renderIframe(dataUrl);
          setLoading(false);
        });
      }
    }

    return () => {
      const parent = document.getElementById("pdfContainer");
      if (parent) parent.innerHTML = "";

      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
    };
  }, [pdfFile]);

  const links = entityId && entityName && prepareLinks({entityId, entityName});
  const ensureBlobReady = async () => {
    if (!links) return;
    if (blobObj) return blobObj;

    if (!links?.post || !links?.get) {
      throw new Error(t("Share functionality is not available for this document"));
    }

    setIsPreparing(true);
    try {
      const {data: shareId} = await instance.post(links.post);
      const {data: blob} = await instance.get(`${links.get}/${shareId}`, {
        responseType: "blob",
      });

      setBlobObj(blob);
      return blob;
    } catch (err) {
      console.error(err);
      toaster.error(t("File preparation error"));
      throw err;
    } finally {
      setIsPreparing(false);
    }
  };

  const getFileNameFromUrl = url => {
    if (!url) return fileName || "document.pdf";
    const parts = url.split("/");
    return parts[parts.length - 1];
  };

  const getPdfBlob = docDefinition => {
    if (!docDefinition) return;
    return new Promise(resolve => {
      pdfMake.createPdf(docDefinition).getBlob(blob => {
        resolve(blob);
      });
    });
  };

  const pdfFileBlob = getPdfBlob(pdfFile);

  const handleShareClick = async () => {
    if (pdfFileBlob) {
      try {
        const file = new File([pdfFileBlob], fileName, {
          title: fileName,
          type: "application/pdf",
        });

        await navigator.share({
          files: [file],
        });
        return;
      } catch (error) {
        console.error(error);
      }
    }

    if (shareLink) {
      const url = `${apiUrl}/${shareLink}`;

      if (navigator.share) {
        try {
          await navigator.share({
            title: fileName,
            text: "Here is your document",
            url,
          });
        } catch (err) {
          console.error(err);
        }
      } else {
        await navigator.clipboard.writeText(url);
        alert("Link copied");
      }

      return;
    }

    try {
      const blob = await ensureBlobReady();

      if (!blob) return;
      const nameFile =
        (fileNameRow && fileName) || (fileArray && fileArray[0] && getFileNameFromUrl(fileArray[0])) || "document.pdf";

      const file = new File([blob], nameFile, {
        type: "application/pdf",
        lastModified: Date.now(),
      });

      if (navigator.share && navigator.canShare && navigator.canShare({files: [file]})) {
        try {
          await navigator.share({
            files: [file],
            text: t("Document"),
          });
          toaster.success(t("Redirect successful"));
        } catch (err) {
          if (err.name === "AbortError") {
            toaster.info(t("Sharing was cancelled"));
          } else {
            console.error(err);
            toaster.error(t("Something went wrong while sharing..."));
          }
        }
      } else {
        toaster.info(t("Web Share API is not supported for this platform"));
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = nameFile;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error(err);
      toaster.error(t("Something went wrong..."));
    }
  };

  return (
    <IconButton
      nameIcon={ICON_NAMES.SHARE}
      onClick={handleShareClick}
      disabled={isPreparing || loading}
      ariaLabel="Share"
      className={className}
      widthIcon={20}
      heightIcon={20}
      tooltipMessage="Share"
      uiPermissionType={uiPermissionType}
    />
  );
};

export default ShareButton;
